/**
 * Playing a story ([Stories](../../docs/STORIES.md),
 * [D-028](../../docs/DECISIONS.md#d-028)).
 *
 * A short scene between Sneakers and one guest, advanced one line at a time by
 * the child, with three to five comprehension checks embedded at the moments
 * they matter. The rules of how it advances are here, pure, because the whole
 * feature rests on one of them:
 *
 * **A check never blocks the story.** Getting one wrong replays the line, gives
 * the answer away warmly, and carries on. A child must never be stuck unable to
 * find out what happens — that would turn the best part of the app into the
 * worst. So there is no state in this file that a wrong answer can reach: the
 * scene advances on the *answer*, not on the *verdict*.
 *
 * It is also **linear**: no choices, no alternate endings. Branching multiplies
 * authoring and audio cost by the number of branches, and at A1–A2 the child is
 * working hard enough to follow one thread. The interactivity that matters is
 * the checks, not the plot.
 */
/**
 * The scene at a given point.
 *
 * `shown` is how many lines have been played, 0 before the first tap. A check
 * is asked once its line has been played and before the next one, which is what
 * "at the moment it matters" means: the question arrives while the answer is
 * still on screen.
 *
 * Out-of-range input clamps rather than throwing. The count comes from a
 * client, and a story that threw would be a child staring at an error instead
 * of a scene.
 */
export function storyStep(lines, checks, shown, 
/**
 * Checks already answered, by their place in `checks`.
 *
 * Not by the line they follow: two checks may follow one line — understand
 * it, then say it — and keying by line marked the second answered with the
 * first, so it was never asked.
 */
answered = new Set()) {
    const at = Math.min(Math.max(Math.trunc(Number.isFinite(shown) ? shown : 0), 0), lines.length);
    const played = lines.slice(0, at);
    const index = checks.findIndex((check, i) => check.afterLine === at && !answered.has(i));
    const found = checks[index];
    const asking = found === undefined ? null : { ...found, index };
    return {
        played,
        asking,
        // Not ended while a check is still open: the wrap-up comes after the last
        // question, not instead of it.
        ended: at >= lines.length && asking === null,
    };
}
export const afterCheck = (correct, afterLine) => ({
    replayLine: correct ? null : afterLine,
    tellThem: !correct,
    carriesOn: true,
});
/**
 * What a finished story pays
 * ([Stories](../../docs/STORIES.md#rewards)).
 *
 * The most effortful lesson in the app, and paid accordingly — three times a
 * normal lesson's XP and a guaranteed silver chest. Not scaled by how many
 * checks were right: a story is not a test, and paying less for a child who
 * needed the answers given to them would punish exactly the child the warm
 * reveal was written for.
 */
export const STORY_XP = 60;
export const STORY_ACORNS = 25;
export const STORY_CHEST = 'silver';
/** The phrases a story names at the end. Three to five, enforced by the linter. */
export const storyTaught = (teaches) => teaches.slice(0, 5);
//# sourceMappingURL=story.js.map